import { useState, useEffect, useMemo } from 'react';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { useInvestments, useCreateInvestment, useUpdateInvestment, useDeleteInvestment, InvestmentAccount, InvestmentFormData } from '@/hooks/useInvestments';
import { useInvestmentTypes } from '@/hooks/useInvestmentTypes';
import { useFetchAccounts } from '@accountHooks';
import { useRiskRatings } from '@/hooks/useRiskRatings';
import { InvestmentOverview } from './overview/InvestmentOverview';
import { InvestmentForm } from '@/components/investment-form/InvestmentForm';
import { InvestmentTransactionForm, InvestmentTransactionFormData } from '@/components/investment-transaction-form/InvestmentTransactionForm';
import { useCreateInvestmentTransaction } from '@/hooks/useInvestmentTransactions';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, ArrowDownToLine, ArrowUpFromLine, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { InvestmentTransactionData } from '@/types/investments';

/**
 * InvestmentsPage component manages the investment tracking feature with tab navigation,
 * form dialogs, and CRUD operations for investment accounts and transactions.
 */
export default function InvestmentsPage() {
  const { data: investments = [], isLoading: investmentsLoading, error: investmentsError } = useInvestments();
  const { data: types = [], isLoading: typesLoading, error: typesError } = useInvestmentTypes();
  const { data: accounts = [] } = useFetchAccounts();
  const { data: riskRatings = [], isError: riskRatingsError } = useRiskRatings();
  
  const createInvestment = useCreateInvestment();
  const updateInvestment = useUpdateInvestment();
  const deleteInvestment = useDeleteInvestment();
  const createTransaction = useCreateInvestmentTransaction();

  const [activeTab, setActiveTab] = useState('overview');
  const [investmentFormOpen, setInvestmentFormOpen] = useState(false);
  const [transactionFormOpen, setTransactionFormOpen] = useState(false);
  const [transactionType, setTransactionType] = useState<'deposit' | 'withdrawal'>('deposit');
  const [editingInvestment, setEditingInvestment] = useState<InvestmentAccount | undefined>();
  const [deletingInvestmentId, setDeletingInvestmentId] = useState<string | null>(null);
  const [preselectedType, setPreselectedType] = useState<string | undefined>();
  const [searchQuery, setSearchQuery] = useState('');

  const filteredInvestments = useMemo(() => {
    if (!searchQuery) return investments;
    const lowerQuery = searchQuery.toLowerCase();
    return investments.filter(inv => 
      inv.fund_name?.toLowerCase().includes(lowerQuery) ||
      inv.fund_manager?.toLowerCase().includes(lowerQuery) ||
      inv.account_number?.toLowerCase().includes(lowerQuery) ||
      inv.member_number?.toLowerCase().includes(lowerQuery) ||
      inv.investment_type?.name?.toLowerCase().includes(lowerQuery)
    );
  }, [investments, searchQuery]);

  const isLoading = investmentsLoading || typesLoading;

  // Show error toasts for query failures
  useEffect(() => {
    if (investmentsError) {
      toast.error('Failed to load investments. Please try again.');
    }
  }, [investmentsError]);

  useEffect(() => {
    if (typesError) {
      toast.error('Failed to load investment types. Please try again.');
    }
  }, [typesError]);

  // Handle add investment from overview or type tab
  const handleAddInvestment = (typeId?: string) => {
    setEditingInvestment(undefined);
    setPreselectedType(typeId);
    setInvestmentFormOpen(true);
  };

  // Handle edit investment
  const handleEditInvestment = (investment: InvestmentAccount) => {
    setEditingInvestment(investment);
    setPreselectedType(undefined);
    setInvestmentFormOpen(true);
  };

  // Handle delete investment
  const handleDeleteInvestment = (id: string) => {
    setDeletingInvestmentId(id);
  };

  const confirmDelete = async () => {
    if (!deletingInvestmentId) return;

    try {
      await deleteInvestment.mutateAsync(deletingInvestmentId);
      toast.success('Investment deleted successfully');
      setDeletingInvestmentId(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete investment';
      toast.error(message);
    }
  };

  // Handle save investment (create or update)
  const handleSaveInvestment = async (data: InvestmentFormData) => {
    try {
      if (editingInvestment) {
        await updateInvestment.mutateAsync({
          id: editingInvestment.id,
          ...data,
        });
        toast.success('Investment updated successfully');
      } else {
        await createInvestment.mutateAsync(data);
        toast.success('Investment created successfully');
      }
      setInvestmentFormOpen(false);
      setEditingInvestment(undefined);
      setPreselectedType(undefined);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save investment';
      toast.error(message);
    }
  };

  // Handle open transaction form
  const handleOpenTransactionForm = (type: 'deposit' | 'withdrawal') => {
    setTransactionType(type);
    setTransactionFormOpen(true);
  };

  // Handle save transaction
  const handleSaveTransaction = async (data: InvestmentTransactionFormData) => {
    try {
      // Convert form data to transaction data (they have the same structure)
      const transactionData: InvestmentTransactionData = {
        transaction_type: data.transaction_type,
        investment_account_id: data.investment_account_id,
        amount: data.amount,
        currency: data.currency,
        source_account_id: data.source_account_id,
        destination_account_id: data.destination_account_id,
        withdrawal_fee: data.withdrawal_fee,
        deposit_fee: data.deposit_fee,
        transaction_date: data.transaction_date,
        notes: data.notes,
        description: data.description,
      };
      
      await createTransaction.mutateAsync(transactionData);
      toast.success(`${data.transaction_type === 'deposit' ? 'Deposit' : 'Withdrawal'} recorded successfully`);
      setTransactionFormOpen(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to record transaction';
      toast.error(message);
    }
  };

  return (
    <ErrorBoundary
      onReset={() => {
        // Reset state and refetch data
        setActiveTab('overview');
        setInvestmentFormOpen(false);
        setTransactionFormOpen(false);
        setEditingInvestment(undefined);
        setDeletingInvestmentId(null);
        setPreselectedType(undefined);
      }}
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Investments</h1>
            <p className="text-muted-foreground">
              Track and manage your investment portfolio
            </p>
          </div>
          <div className="flex flex-col sm:flex-row flex-wrap items-center gap-2 w-full sm:w-auto">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search investments..."
                className="pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleOpenTransactionForm('deposit')}
                disabled={investments.length === 0}
                aria-label="Deposit funds to investment account"
              >
                <ArrowDownToLine className="mr-2 h-4 w-4" aria-hidden="true" />
                Deposit
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleOpenTransactionForm('withdrawal')}
                disabled={investments.length === 0}
                aria-label="Withdraw funds from investment account"
              >
                <ArrowUpFromLine className="mr-2 h-4 w-4" aria-hidden="true" />
                Withdraw
              </Button>
              <Button onClick={() => handleAddInvestment()} aria-label="Add new investment account">
                <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                Add Investment
              </Button>
            </div>
          </div>
        </div>

        <InvestmentOverview
          investments={filteredInvestments}
          types={types}
          onAddInvestment={() => handleAddInvestment()}
          onEditInvestment={handleEditInvestment}
          onDeleteInvestment={handleDeleteInvestment}
          isLoading={isLoading}
        />

        {/* Investment Form Dialog */}
        <Dialog
          open={investmentFormOpen}
          onOpenChange={(open) => {
            setInvestmentFormOpen(open);
            if (!open) {
              setEditingInvestment(undefined);
              setPreselectedType(undefined);
            }
          }}
        >
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingInvestment ? 'Edit Investment' : 'Add Investment'}
              </DialogTitle>
            </DialogHeader>
            <InvestmentForm
              investment={editingInvestment}
              preselectedType={preselectedType}
              investmentTypes={types}
              riskRatings={riskRatings}
              riskRatingsError={riskRatingsError}
              onSave={handleSaveInvestment}
              onCancel={() => {
                setInvestmentFormOpen(false);
                setEditingInvestment(undefined);
                setPreselectedType(undefined);
              }}
              isSubmitting={createInvestment.isPending || updateInvestment.isPending}
            />
          </DialogContent>
        </Dialog>

        {/* Transaction Form Dialog */}
        <Dialog
          open={transactionFormOpen}
          onOpenChange={(open) => {
            setTransactionFormOpen(open);
          }}
        >
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {transactionType === 'deposit' ? 'Deposit Funds' : 'Withdraw Funds'}
              </DialogTitle>
            </DialogHeader>
            <InvestmentTransactionForm
              transactionType={transactionType}
              investments={investments}
              accounts={accounts}
              onSave={handleSaveTransaction}
              onCancel={() => setTransactionFormOpen(false)}
              isSubmitting={createTransaction.isPending}
            />
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog
          open={!!deletingInvestmentId}
          onOpenChange={(open) => !open && setDeletingInvestmentId(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Investment?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete this investment account. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </ErrorBoundary>
  );
}
